import { ArrowForward } from '@mui/icons-material';
import { Box, Button, Stack, Typography } from '@mui/material';
import { Link } from 'react-router';

import { url } from '../../appRouter/url.ts';
import { AccordionWithHeading, TrpcLoadingInfo } from '../../components/general/index.js';
import { GetDurchgaengeByAkaThemaIdsOutput, sapStatus, ThemaFromThemaRouter } from '../../dtos.js';
import { trpc } from '../../trpc.js';
import { formatDate, getExperteName } from '../../utils/index.js';
import { useOnlinePraesenzFilter } from './hooks/useOnlinePraesenzFilter.js';
import { usePaginatedList } from './hooks/usePaginatedList.js';
import { OnlinePraesenzFilter } from './OnlinePraesenzFilter.js';
import { VeranstaltungOrt } from './VeranstaltungOrt.js';
import { VeranstaltungTrainer } from './VeranstaltungTrainer.js';

export const DurchgaengeList = ({ thema }: { thema: ThemaFromThemaRouter }) => {
  const durchgaengeQuery = trpc.durchgang.getDurchgaengeByAkaThemaIds.useQuery({
    akaThemaIds: [thema.akaThemaId, ...thema.verknuepfteThemen.map((pendant) => pendant.akaThemaId)],
  });
  const { data: durchgaenge } = durchgaengeQuery;
  const { displayedCount, showMore } = usePaginatedList();

  const onlinePraesenzFilter = useOnlinePraesenzFilter(thema);
  const { selectedOptions } = onlinePraesenzFilter;

  const filteredDurchgaenge = durchgaenge
    ? durchgaenge.filter((durchgang) => selectedOptions.includes(durchgang.akaThemaId))
    : [];

  return (
    <AccordionWithHeading heading="Durchgänge" bordered={false}>
      <TrpcLoadingInfo trpcQuery={durchgaengeQuery} entity="Durchgänge">
        <OnlinePraesenzFilter thema={thema} {...onlinePraesenzFilter} />
        <Stack direction="column" gap={1.5} pt={1.5}>
          {filteredDurchgaenge.length === 0 && (
            <Typography variant="labelLight" color="textTertiary" textAlign="center" pt={1}>
              Wähle aus, ob du Präsenz- und/oder Online-Durchgänge sehen möchtest.
            </Typography>
          )}
          {filteredDurchgaenge.length > 0 &&
            filteredDurchgaenge
              .slice(0, displayedCount)
              .map((durchgang) => <DurchgangCard key={durchgang.id} durchgang={durchgang} />)}
          {filteredDurchgaenge.length > displayedCount && (
            <Button onClick={showMore} sx={{ color: 'text.primary' }}>
              Mehr anzeigen
            </Button>
          )}
        </Stack>
      </TrpcLoadingInfo>
    </AccordionWithHeading>
  );
};

const getVeranstaltungenDateRange = (durchgang: GetDurchgaengeByAkaThemaIdsOutput[number]) => (
  <Typography
    variant="labelLight"
    color={durchgang.sapStatus === sapStatus.ABGESAGT ? 'textTertiary' : undefined}
    sx={{ textDecoration: durchgang.sapStatus === sapStatus.ABGESAGT ? 'line-through' : undefined }}
  >
    {formatDate(durchgang.start) + ' - ' + formatDate(durchgang.end, true)}
  </Typography>
);

const getVeranstaltungenOrte = (veranstaltungen: GetDurchgaengeByAkaThemaIdsOutput[number]['modultermine']) => {
  // Ort:
  // - wenn Präsenz mit Online, dann nur Anzeige der Präsenz-Orte (location.kuerzel) (wenn es mehr als einen gibt, dann werden alle angezeigt)
  // - wenn Präsenz, dann Anzeige der Präsenz-Orte (location.kuerzel)(wenn es mehr als einen gibt, dann werden alle angezeigt)
  // - wenn Online, dann Anzeige des Meeting-Tools (planung.veranstaltung.online_tool)
  const isFullyOnline = veranstaltungen.every((va) => Boolean(va.onlineTool));

  const orte = new Set<string>();
  for (const veranstaltung of veranstaltungen) {
    if (isFullyOnline && veranstaltung.onlineTool) {
      orte.add(veranstaltung.onlineTool);
    } else {
      for (const blockung of veranstaltung.raumBlockungen) {
        orte.add(blockung.raum.location.kuerzel);
      }
    }
  }
  return Array.from(orte);
};

export const getVeranstaltungenTrainers = (
  veranstaltungen: GetDurchgaengeByAkaThemaIdsOutput[number]['modultermine'],
) => {
  const allTrainers = new Set<string>();
  for (const veranstaltung of veranstaltungen) {
    for (const blockung of veranstaltung.experteBlockungen) {
      allTrainers.add(getExperteName(blockung.experte));
    }
  }
  return Array.from(allTrainers);
};

const DurchgangCard = function ({ durchgang }: { durchgang: GetDurchgaengeByAkaThemaIdsOutput[number] }) {
  return (
    <Stack
      direction="row"
      sx={{
        backgroundColor: durchgang.sapStatus === sapStatus.ABGESAGT ? 'surface.background' : 'surface.brandLight',
        p: 1,
        borderRadius: '4px',
        border: (theme) =>
          durchgang.sapStatus === sapStatus.ABGESAGT ? `1px solid ${theme.palette.border.subtle}` : undefined,
      }}
      alignItems="center"
      justifyContent="space-between"
    >
      <Stack direction="column" spacing={0.5} flexGrow={1} width="calc(100% - 24px)">
        {getVeranstaltungenDateRange(durchgang)}
        <Box display="grid" gap={0.5} gridTemplateColumns="1fr 2fr">
          <VeranstaltungOrt
            online={durchgang.modultermine.every((va) => Boolean(va.onlineTool))}
            orte={getVeranstaltungenOrte(durchgang.modultermine)}
          />
          <VeranstaltungTrainer trainers={getVeranstaltungenTrainers(durchgang.modultermine)} />
        </Box>
      </Stack>
      <Link to={url.durchgang.details(durchgang.akaDurchgangId)} target="_blank">
        <ArrowForward />
      </Link>
    </Stack>
  );
};
